import { useEffect, useMemo, useRef, useState } from "react";
import type { DragEvent } from "react";
import { api } from "./api";
import {
  appendRelativeFolder,
  documentsBelow,
  fileExtension,
  folderDepth,
  folderName,
  folderPaths,
  formatFileDate,
  formatFileSize,
  immediateChildFolders,
  normalizeFolderPath,
} from "./matter-files";
import type { Document, Matter } from "./types";

interface MatterFilesDialogProps {
  matter: Matter;
  documents: Document[];
  onClose: () => void;
  onDocumentsChange: (documents: Document[]) => void;
}

export function MatterFilesDialog({
  matter,
  documents,
  onClose,
  onDocumentsChange,
}: MatterFilesDialogProps) {
  const [activeFolder, setActiveFolder] = useState("/");
  const [query, setQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [moveTarget, setMoveTarget] = useState("/");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const filesInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (deleteConfirmOpen) setDeleteConfirmOpen(false);
      else onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [deleteConfirmOpen, onClose]);

  useEffect(() => {
    folderInputRef.current?.setAttribute("webkitdirectory", "");
  }, []);

  const paths = useMemo(() => folderPaths(documents), [documents]);
  const childFolders = useMemo(
    () => immediateChildFolders(paths, activeFolder),
    [activeFolder, paths],
  );
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleDocuments = useMemo(() => documents
    .filter((document) => normalizedQuery
      ? document.fileName.toLocaleLowerCase().includes(normalizedQuery)
      : document.folderPath === activeFolder)
    .sort((left, right) => left.fileName.localeCompare(right.fileName)),
  [activeFolder, documents, normalizedQuery]);
  const selectedDocuments = useMemo(
    () => documents.filter((document) => selectedIds.has(document.id)),
    [documents, selectedIds],
  );

  const breadcrumbPaths = useMemo(() => {
    const result = [{ path: "/", name: "Matter files" }];
    let path = "/";
    for (const segment of activeFolder.split("/").filter(Boolean)) {
      path += `${segment}/`;
      result.push({ path, name: segment });
    }
    return result;
  }, [activeFolder]);

  const updateDocument = (next: Document) => {
    onDocumentsChange(documents.map((document) => document.id === next.id ? next : document));
  };

  const uploadPairs = async (pairs: Array<{ file: File; relativeFolder: string }>) => {
    if (pairs.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    let nextDocuments = documents;
    const failures: string[] = [];
    for (const { file, relativeFolder } of pairs) {
      const folderPath = appendRelativeFolder(activeFolder, relativeFolder);
      if (!folderPath) {
        failures.push(`${file.name}: its folder name contains an unsupported character.`);
        continue;
      }
      try {
        const result = await api.uploadDocument(matter.id, file, folderPath);
        nextDocuments = [result.document, ...nextDocuments];
        onDocumentsChange(nextDocuments);
      } catch (caught) {
        failures.push(`${file.name}: ${caught instanceof Error ? caught.message : "Upload failed."}`);
      }
    }
    if (failures.length > 0) setError(failures.join(" "));
    setBusy(false);
    if (filesInputRef.current) filesInputRef.current.value = "";
    if (folderInputRef.current) folderInputRef.current.value = "";
  };

  const uploadFiles = (files: FileList | null) => {
    if (!files) return;
    void uploadPairs(Array.from(files).map((file) => ({ file, relativeFolder: "" })));
  };

  const uploadFolder = (files: FileList | null) => {
    if (!files) return;
    void uploadPairs(Array.from(files).map((file) => {
      const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath ?? "";
      const relativeFolder = relativePath.split("/").slice(0, -1).join("/");
      return { file, relativeFolder: relativeFolder ? `${relativeFolder}/` : "" };
    }));
  };

  const dropFiles = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    uploadFiles(event.dataTransfer.files);
  };

  const toggleSelected = (documentId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(documentId)) next.delete(documentId);
      else next.add(documentId);
      return next;
    });
  };

  const moveSelected = async () => {
    const target = normalizeFolderPath(moveTarget);
    if (!target) {
      setError("Folder names cannot contain \\ < > : \" | ? * or control characters.");
      return;
    }
    if (selectedIds.size === 0 || busy) return;
    setBusy(true);
    setError(null);
    let nextDocuments = documents;
    const failedIds = new Set<string>();
    for (const documentId of selectedIds) {
      try {
        const result = await api.moveDocument(documentId, target);
        nextDocuments = nextDocuments.map((document) =>
          document.id === result.document.id ? result.document : document);
        onDocumentsChange(nextDocuments);
      } catch (caught) {
        failedIds.add(documentId);
        setError(caught instanceof Error ? caught.message : "A file could not be moved.");
      }
    }
    setSelectedIds(failedIds);
    setMoveTarget(target);
    setBusy(false);
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0 || busy) return;
    setBusy(true);
    setError(null);
    let nextDocuments = documents;
    const failedIds = new Set<string>();
    const failures: string[] = [];
    for (const document of selectedDocuments) {
      try {
        await api.deleteDocument(document.id);
        nextDocuments = nextDocuments.filter((item) => item.id !== document.id);
      } catch (caught) {
        failedIds.add(document.id);
        failures.push(`${document.fileName}: ${caught instanceof Error ? caught.message : "Delete failed."}`);
      }
    }
    onDocumentsChange(nextDocuments);
    setSelectedIds(failedIds);
    setDeleteConfirmOpen(false);
    if (failures.length > 0) setError(failures.join(" "));
    setBusy(false);
  };

  const togglePriorWork = async (document: Document) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.setPriorWorkProduct(document.id, !document.isPriorWorkProduct);
      updateDocument(result.document);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The work-product designation could not be changed.");
    } finally {
      setBusy(false);
    }
  };

  const selectFolder = (path: string) => {
    setActiveFolder(path);
    setQuery("");
    setSelectedIds(new Set());
    setMoveTarget(path);
  };

  return (
    <div className="matter-files-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="matter-files-dialog" role="dialog" aria-modal="true" aria-labelledby="matter-files-title">
        <header>
          <div>
            <span>{matter.matterNumber}</span>
            <h2 id="matter-files-title">Matter files</h2>
            <p>{matter.name} · {documents.length} {documents.length === 1 ? "file" : "files"}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close matter files">×</button>
        </header>

        <div className="matter-files-layout">
          <aside className="matter-files-sidebar">
            <strong>Folders</strong>
            <nav aria-label="Matter file folders">
              {paths.map((path) => (
                <button
                  className={path === activeFolder ? "active" : ""}
                  key={path}
                  onClick={() => selectFolder(path)}
                  style={{ paddingLeft: `${10 + folderDepth(path) * 15}px` }}
                  type="button"
                >
                  <span>▰</span>
                  <b>{folderName(path)}</b>
                  <small>{documentsBelow(documents, path)}</small>
                </button>
              ))}
            </nav>
            <p>New folders appear when files are uploaded or moved into a new path.</p>
          </aside>

          <main
            className={`matter-files-browser${dragging ? " dragging" : ""}`}
            onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => event.currentTarget === event.target && setDragging(false)}
            onDrop={dropFiles}
          >
            <div className="matter-files-toolbar">
              <div className="matter-files-breadcrumbs">
                {breadcrumbPaths.map((crumb, index) => (
                  <span key={crumb.path}>
                    {index > 0 && <i>›</i>}
                    <button type="button" onClick={() => selectFolder(crumb.path)}>{crumb.name}</button>
                  </span>
                ))}
              </div>
              <div className="matter-files-actions">
                <label className="matter-files-search"><span>⌕</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search file names" /></label>
                <button className="secondary-button" type="button" disabled={busy} onClick={() => folderInputRef.current?.click()}>Upload folder</button>
                <button className="primary-button" type="button" disabled={busy} onClick={() => filesInputRef.current?.click()}>{busy ? "Working…" : "Upload files"}</button>
                <input ref={filesInputRef} type="file" multiple hidden onChange={(event) => uploadFiles(event.target.files)} />
                <input ref={folderInputRef} type="file" multiple hidden onChange={(event) => uploadFolder(event.target.files)} />
              </div>
            </div>

            {error && <div className="matter-files-error">{error}<button type="button" onClick={() => setError(null)}>×</button></div>}

            {selectedIds.size > 0 && (
              <div className="matter-files-move-bar">
                <strong>{selectedIds.size} selected</strong>
                <label>
                  <span>Move to</span>
                  <input list="matter-file-folder-paths" value={moveTarget} onChange={(event) => setMoveTarget(event.target.value)} placeholder="/Discovery/Drafts/" />
                  <datalist id="matter-file-folder-paths">{paths.map((path) => <option value={path} key={path} />)}</datalist>
                </label>
                <button className="primary-button" type="button" disabled={busy} onClick={() => void moveSelected()}>Move selected</button>
                <button className="danger-button" type="button" disabled={busy} onClick={() => setDeleteConfirmOpen(true)}>Delete</button>
                <button className="secondary-button" type="button" disabled={busy} onClick={() => setSelectedIds(new Set())}>Cancel</button>
              </div>
            )}

            <div className="matter-files-content">
              {!normalizedQuery && childFolders.length > 0 && (
                <section className="matter-folder-grid" aria-label="Folders">
                  {childFolders.map((path) => (
                    <button type="button" key={path} onClick={() => selectFolder(path)}>
                      <span>▰</span>
                      <strong>{folderName(path)}</strong>
                      <small>{documentsBelow(documents, path)} {documentsBelow(documents, path) === 1 ? "file" : "files"}</small>
                    </button>
                  ))}
                </section>
              )}

              <section className="matter-file-list">
                <header>
                  <strong>{normalizedQuery ? "Search results" : folderName(activeFolder)}</strong>
                  <span>{visibleDocuments.length} {visibleDocuments.length === 1 ? "file" : "files"}</span>
                </header>
                {visibleDocuments.map((document) => (
                  <div className={selectedIds.has(document.id) ? "selected" : ""} key={document.id}>
                    <input type="checkbox" checked={selectedIds.has(document.id)} onChange={() => toggleSelected(document.id)} aria-label={`Select ${document.fileName}`} />
                    <span className="file-icon">{fileExtension(document.fileName)}</span>
                    <a href={`/api/documents/${document.id}/download`}>
                      <strong>{document.fileName}</strong>
                      <small>{formatFileSize(document.fileSizeBytes)} · {document.type} · {formatFileDate(document.uploadedDate)}{normalizedQuery ? ` · ${document.folderPath}` : ""}</small>
                    </a>
                    <button className={document.isPriorWorkProduct ? "reusable active" : "reusable"} type="button" disabled={busy} onClick={() => void togglePriorWork(document)} title="Control whether this file can be used as reusable prior work product">{document.isPriorWorkProduct ? "Reusable" : "Mark reusable"}</button>
                    <a className="matter-file-download" href={`/api/documents/${document.id}/download`} aria-label={`Download ${document.fileName}`}>↓</a>
                  </div>
                ))}
                {visibleDocuments.length === 0 && (
                  <div className="matter-files-empty">
                    <span>{normalizedQuery ? "⌕" : "⇧"}</span>
                    <strong>{normalizedQuery ? "No matching files" : "This folder is empty"}</strong>
                    <p>{normalizedQuery ? "Try another filename." : "Drop files here or use Upload files to add documents to this folder."}</p>
                  </div>
                )}
              </section>
            </div>

            {dragging && <div className="matter-files-drop-zone">Drop files into {folderName(activeFolder)}</div>}
          </main>
        </div>

        {deleteConfirmOpen && (
          <div className="matter-files-confirm-backdrop" onMouseDown={(event) => event.target === event.currentTarget && setDeleteConfirmOpen(false)}>
            <section className="matter-files-confirm" role="alertdialog" aria-modal="true" aria-labelledby="matter-files-delete-title" aria-describedby="matter-files-delete-description">
              <span>Delete {selectedDocuments.length === 1 ? "file" : "files"}</span>
              <h3 id="matter-files-delete-title">Remove {selectedDocuments.length} {selectedDocuments.length === 1 ? "file" : "files"} from this matter?</h3>
              <p id="matter-files-delete-description">The selected files will disappear from the matter library. This cannot be undone from the app.</p>
              <ul>
                {selectedDocuments.slice(0, 5).map((document) => <li key={document.id}>{document.fileName}</li>)}
                {selectedDocuments.length > 5 && <li>and {selectedDocuments.length - 5} more…</li>}
              </ul>
              <footer>
                <button className="secondary-button" type="button" disabled={busy} onClick={() => setDeleteConfirmOpen(false)}>Keep files</button>
                <button className="danger-button" type="button" disabled={busy} onClick={() => void deleteSelected()}>{busy ? "Deleting…" : "Delete files"}</button>
              </footer>
            </section>
          </div>
        )}
      </section>
    </div>
  );
}
